/**
 * Personal Flight Logbook - Classic pilot logbook format
 * Shows pilot's personal flight history with export capabilities
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '@/services/authService';
import { API_BASE_URL } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';

interface LogbookEntry {
  id: number;
  flightDate: string;
  tailNumber: string;
  aircraftType: string;
  fromAirport: string;
  toAirport: string;
  actualFlightTime: number;
  actualAirTime: number;
  pilotRole: 'PIC' | 'SIC'; // Pilot in Command or Second in Command
  engineType: 'ME' | 'SE'; // Multi-Engine or Single-Engine
  isMultiEngine: boolean;
  logbookPhotoUrl?: string;
  pilotNotes?: string;
}

interface LogbookStats {
  totalFlightTime: number;
  totalAirTime: number;
  totalFlights: number;
  multiEngineTime: number;
  singleEngineTime: number;
  picTime: number;
  sicTime: number;
  ytdFlightTime: number;
}

export const LogbookPage: React.FC = () => {
  const { showError } = useToast();

  const [entries, setEntries] = useState<LogbookEntry[]>([]);
  const [stats, setStats] = useState<LogbookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadLogbook();
  }, []);

  const loadLogbook = async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const response = await authFetch(`${API_BASE_URL}/logbook/personal`);
      const data = await response.json();

      if (data.success) {
        setEntries(data.data.entries);
        setStats(data.data.stats);
      } else {
        setLoadFailed(true);
        showError('Load Failed', 'Failed to load logbook');
      }
    } catch {
      setLoadFailed(true);
      showError('Load Error', 'Error loading logbook');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/logbook/personal/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dateFilter)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logbook-${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        showError('Export Failed', 'Failed to export logbook');
      }
    } catch {
      showError('Export Error', 'Error exporting logbook');
    }
  };

  const formatTime = (decimal: number) => {
    return decimal.toFixed(1);
  };

  const formatDate = (dateString: string) => {
    const [y, m, d] = dateString.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (loadFailed && entries.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground font-medium mb-2">Failed to load logbook</p>
          <button onClick={loadLogbook} className="text-primary hover:underline text-sm">Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-card rounded-lg shadow border border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-foreground">My Flights</h1>
            <button
              onClick={exportToPDF}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              Export PDF
            </button>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-700 dark:text-blue-400">Total Flight Time</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatTime(stats.totalFlightTime)}</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-700 dark:text-green-400">Total Flights</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.totalFlights}</div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <div className="text-sm text-purple-700 dark:text-purple-400">PIC Time</div>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatTime(stats.picTime)}</div>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <div className="text-sm text-orange-700 dark:text-orange-400">Multi-Engine</div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatTime(stats.multiEngineTime)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Date Filters */}
        <div className="bg-card rounded-lg shadow border border-border p-4 mb-6">
          <div className="flex gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                className="border border-border bg-background text-foreground rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">End Date</label>
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                className="border border-border bg-background text-foreground rounded px-3 py-2"
              />
            </div>
            <button
              onClick={loadLogbook}
              className="bg-muted text-foreground px-4 py-2 rounded hover:bg-muted/80 transition-colors"
            >
              Filter
            </button>
          </div>
        </div>

        {/* Logbook Table */}
        <div className="bg-card rounded-lg shadow border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Tail #</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Route</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Flight Time</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Engine</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">
                      {formatDate(entry.flightDate)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {entry.tailNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {entry.aircraftType}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {entry.fromAirport} → {entry.toAirport}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        entry.pilotRole === 'PIC'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400'
                      }`}>
                        {entry.pilotRole}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-foreground">
                      {formatTime(entry.actualFlightTime)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        entry.engineType === 'ME'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-400'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {entry.engineType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                      {entry.pilotNotes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {entries.length === 0 && (
            <div className="text-center py-12">
              <div className="text-muted-foreground">No flight entries found</div>
              <div className="text-sm text-muted-foreground/70 mt-1">Complete some flights to build your logbook</div>
            </div>
          )}
        </div>

        {/* Summary at bottom */}
        {stats && (
          <div className="bg-card rounded-lg shadow border border-border p-6 mt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Flight Time Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Total Flight Time</div>
                <div className="font-mono font-semibold text-foreground">{formatTime(stats.totalFlightTime)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">PIC Time</div>
                <div className="font-mono font-semibold text-foreground">{formatTime(stats.picTime)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">SIC Time</div>
                <div className="font-mono font-semibold text-foreground">{formatTime(stats.sicTime)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Multi-Engine</div>
                <div className="font-mono font-semibold text-foreground">{formatTime(stats.multiEngineTime)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Single-Engine</div>
                <div className="font-mono font-semibold text-foreground">{formatTime(stats.singleEngineTime)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">YTD Flight Time</div>
                <div className="font-mono font-semibold text-foreground">{formatTime(stats.ytdFlightTime)}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};